// Thin fetch wrapper with typed error. Every API call in the dashboard
// goes through `apiFetch` — so error handling, CORS, and content types
// live in one place instead of being duplicated across each hook.

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** Seconds from a Retry-After header (429/503), else null. */
  retryAfter: number | null;
  /** The request path ("/api/auth/me"), when apiFetch made the call. */
  path: string | null;

  constructor(
    status: number,
    message: string,
    body: unknown,
    retryAfter: number | null = null,
    path: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.retryAfter = retryAfter;
    this.path = path;
  }
}

/** Retry-After in seconds; the HTTP-date form is not one the api sends. */
export function retryAfterSeconds(header: string | null): number | null {
  if (header === null || !/^\s*\d+\s*$/.test(header)) return null;
  return Number.parseInt(header, 10);
}

function errorMessageFor(status: number, path: string, body: unknown): string {
  const detail = body && typeof body === "object" && "detail" in body
    ? (body as { detail?: unknown }).detail
    : body;
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    const code = typeof record.error === "string" ? record.error : null;
    const message = typeof record.message === "string" ? record.message : null;
    if (code && message) return `${code}: ${message}`;
    if (message) return message;
    if (code) return code;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  return `${path} → ${status}`;
}

export interface ApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Extra request headers (e.g. Idempotency-Key), merged over Content-Type. */
  headers?: Record<string, string>;
}

export async function apiFetch<T>(
  path: string,
  opts: ApiFetchOptions = {},
): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      ...opts.headers,
    },
    credentials: "include",
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    // Callers pass objects; a few historically passed JSON.stringify(...)
    // already, which then got encoded twice and 422'd at the server
    // ("Input should be a valid dictionary"). A string body is sent as-is.
    init.body = typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body);
  }
  const resp = await fetch(path, init);

  // Read body once. We try JSON first; if it isn't valid JSON (e.g.
  // an HTML error page from the proxy) we fall back to text and
  // wrap it so the caller sees something useful.
  const text = await resp.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { error: "non_json_response", snippet: text.slice(0, 400) };
    }
  }

  if (!resp.ok) {
    throw new ApiError(
      resp.status,
      errorMessageFor(resp.status, `${init.method} ${path}`, parsed),
      parsed,
      retryAfterSeconds(resp.headers.get("Retry-After")),
      path,
    );
  }
  return parsed as T;
}

// ─── Saying what went wrong ───
//
// errorText(e) is the one sentence a person reads: never a code, a path or
// a socket message. errorDetail(e) is the raw text for a "Details" fold
// (support needs it). errorCode / isUnreachable / googleReason classify an
// error for a component that shows a different state (LoadError,
// GoogleMissing). Status first, message matching only as a fallback.

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** The server's error code: body.detail.error, else body.error, else null. */
export function errorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const body = asRecord(e.body);
  const detail = asRecord(body?.detail);
  if (typeof detail?.error === "string") return detail.error;
  if (typeof body?.error === "string") return body.error;
  return null;
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  return "";
}

/** A fetch that never reached the server (offline, DNS, CORS, a dropped connection). */
function isNetworkFailure(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 0;
  if (!(e instanceof TypeError)) return false;
  return /failed to fetch|networkerror|load failed|fetch failed/i.test(e.message);
}

/** The server, or something behind it, did not answer: a neutral state, not a red one. */
export function isUnreachable(e: unknown): boolean {
  if (isNetworkFailure(e)) return true;
  if (e instanceof ApiError && (e.status === 502 || e.status === 503 || e.status === 504)) return true;
  if (errorCode(e) === "non_json_response") return true;
  return /gateway unreachable|connection refused/i.test(messageOf(e));
}

/** Why Google is missing for an agent, or null when the error is about something else. */
export function googleReason(e: unknown): "not_connected" | "revoked" | null {
  const code = errorCode(e);
  if (code === "google_not_connected") return "not_connected";
  if (code === "google_token_revoked") return "revoked";
  return null;
}

const SNAKE_CODE = /^[a-z0-9]+(?:[_.-][a-z0-9]+)*$/;

/** The server's own words when they read as a sentence, else null. */
function serverSentence(e: ApiError): string | null {
  const body = asRecord(e.body);
  const detail = body?.detail;
  const candidates = [asRecord(detail)?.message, typeof detail === "string" ? detail : null, body?.message];
  // The message apiFetch built ("code: words"), minus its code; never its
  // own fallback, which is a path ("POST /api/x → 422").
  if (!/→ \d{3}$/.test(e.message)) candidates.push(e.message.replace(/^[a-z0-9_]+: /, ""));
  for (const c of candidates) {
    if (typeof c !== "string") continue;
    const text = c.trim();
    if (text.includes(" ") && !SNAKE_CODE.test(text)) return text;
  }
  return null;
}

/** One plain sentence for a person: what happened and what to do. */
export function errorText(e: unknown): string {
  if (isNetworkFailure(e)) return "Can't reach the server. Check your connection, then try again.";
  if (!(e instanceof ApiError)) return "Something went wrong. Try again.";
  switch (errorCode(e)) {
    case "google_not_connected":
      return "This agent isn't signed in to Google.";
    case "google_token_revoked":
      return "This agent's Google sign-in has expired.";
    case "google_auth_transient":
    case "google_api_transient":
      return "Google is slow to answer. Try again in a moment.";
    case "customer_not_deployed":
      return "This workspace isn't set up yet.";
    case "agent_not_found":
      return "That agent no longer exists.";
  }
  const s = e.status;
  // Lattice restarting, or an agent's gateway behind it down: say only what is true of both.
  if (s === 502 || s === 503 || s === 504 || errorCode(e) === "non_json_response") {
    return "Didn't get an answer just now. Try again in a moment.";
  }
  if (s === 401) return "Your session has ended. Sign in again.";
  if (s === 403) return "You don't have access to this.";
  if (s === 404) return "This isn't there any more.";
  if (s === 429) return "Too many requests at once. Try again in a minute.";
  if (s === 400 || s === 409 || s === 422) return serverSentence(e) ?? "That didn't go through. Try again.";
  if (s >= 500) return `Something went wrong on the server (error ${s}). Try again.`;
  return "Something went wrong. Try again.";
}

/** The raw text behind errorText, for a Details fold; null when it adds nothing. */
export function errorDetail(e: unknown): string | null {
  const raw = messageOf(e).trim();
  if (!raw || raw === errorText(e)) return null;
  return raw;
}
